import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
    Text,
    Content,
    Col,
    Row,
    Grid,
    FlexboxGrid,
    Button,
    Form,
    Stack,
} from 'rsuite'
import {
    updatePermitData,
} from '../../store/slices/adminUpdate/adminUpdateSlice'
import FlowPageLayout from '../../components/layout/FlowPageLayout'
import PortalHeader from '../../components/layout/PortalHeader'
import {
    emailRequiredValidation,
    requiredBooleanValidation,
} from '../../common/validation'
import { ADMIN_UPDATE_ACCOUNT_OPTIONS_ENUM, FLOWS_ENUM } from '../../common/enums'
import { selectAdminUpdatedPermitData } from '../../store/slices/adminUpdate/adminUpdateSelectors'
import Notice from '../../components/user-feedback/Notice'
import { selectConfigExpressRegistrationTypeVerbiage } from '../../store/slices/config/configSelectors'
import pluralize from 'pluralize'
import FormTextField from '../../components/form/FormTextField'
import RadioQuestionGroup from '../../components/form/RadioQuestionGroup'
import { getUserDetailsFromEmailAddress, validateCognitoEmailAddress } from '../../store/slices/adminUpdate/adminUpdateThunks'
import { CheckRound } from '@rsuite/icons'
import IconIndicator from '../../components/user-feedback/IconIndicator'

const AdminUpdatePortalAccount = () => {
    // State and dependencies
    const dispatch = useDispatch();

    const form = useRef();
    const [formValues, setFormValues] = useState({});
    const [formError, setFormError] = useState({});
    const [verifyEmailInProgress, setVerifyEmailInProgress] = useState(false);
    const [hasVerifiedEmail, setHasVerifiedEmail] = useState(true);
    const [showNotVerifiedWarning, setShowNotVerifiedWarning] = useState(false);
    const [linkedPermitsWarning, setLinkedPermitsWarning] = useState("");
    const [accountAlreadyExistsWithEmail, setAccountAlreadyExistsWithEmail] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    // Selectors
    const licenseTypeVerbiage = useSelector(selectConfigExpressRegistrationTypeVerbiage);
    const { registrant, updateAllPermits, initialRegistrantData, updateAccountOption } = useSelector(selectAdminUpdatedPermitData);

    const C_ALL_PERMITS = true;
    const C_THIS_PERMIT_ONLY = false;

    const emailUpdateOptions = useMemo(() => {
        return [
            { key: C_ALL_PERMITS, label: `Update for all ${pluralize(licenseTypeVerbiage.toLowerCase())}` },
            { key: C_THIS_PERMIT_ONLY, label: `Update for this ${licenseTypeVerbiage.toLowerCase()} only (move to new Customer Portal Account)` }
        ];
    }, [C_ALL_PERMITS, C_THIS_PERMIT_ONLY, licenseTypeVerbiage])

    const formModel = useMemo(() => {
        return {
            email: registrant?.registrant?.email,
            updateAllPermits: updateAllPermits
        };
    }, [registrant, updateAllPermits]);

    const validationModel = {
        email: emailRequiredValidation,
        updateAllPermits: requiredBooleanValidation
    };

    useEffect(() => {
        if (registrant.registrant) {
            setFormValues(
                {
                    email: registrant?.registrant?.email,
                    updateAllPermits: updateAllPermits
                }
            );
        }
    }, [registrant, updateAllPermits]);

    useEffect(() => {
        const accountAlreadyExists = updateAccountOption && updateAccountOption === ADMIN_UPDATE_ACCOUNT_OPTIONS_ENUM.MOVETOEXISTINGACCOUNT
        setAccountAlreadyExistsWithEmail(accountAlreadyExists);
    }, [updateAccountOption]);

    useEffect(() => {
        const populateLinkedPermitWarning = async () => {
            if (initialRegistrantData?.email) {
                setIsLoading(true);
                const detailsOfAccountMovingTo = await dispatch(getUserDetailsFromEmailAddress(initialRegistrantData.email));

                if (detailsOfAccountMovingTo.successful) {
                    const numLicenses = detailsOfAccountMovingTo?.result?.licenseCount;
                    setLinkedPermitsWarning(<Text>Please note that this registrant's portal account is linked to <strong>{numLicenses}</strong> active {pluralize(licenseTypeVerbiage.toLowerCase(), numLicenses)}. All {pluralize(licenseTypeVerbiage.toLowerCase())} (including historical {pluralize(licenseTypeVerbiage.toLowerCase())}) will be moved to the new email address and this user will need to use the updated email address to login in future.</Text>);
                }
                else {
                    setLinkedPermitsWarning(<Text>Please note, this registrant's portal account may be linked to other {pluralize(licenseTypeVerbiage.toLowerCase())}, All {pluralize(licenseTypeVerbiage.toLowerCase())} (including historical {pluralize(licenseTypeVerbiage.toLowerCase())}) will be moved to the new email address and this user will need to use the updated email address to login in future.</Text>);
                }
                setIsLoading(false);
            }
        };

        populateLinkedPermitWarning();
    }, [initialRegistrantData, dispatch]);

    const emailAddressChanged = useMemo(() => {
        return formValues?.email?.toLowerCase() !== initialRegistrantData?.email?.toLowerCase();
    }, [formValues, initialRegistrantData]);

    const showVerifyButton = useMemo(() => {
        if (!verifyEmailInProgress && !hasVerifiedEmail && emailAddressChanged) {
            return true;
        }
        else {
            return false;
        }
    }, [emailAddressChanged, verifyEmailInProgress, hasVerifiedEmail]);

    const showCheck = useMemo(() => {
        if ((!verifyEmailInProgress && hasVerifiedEmail) || !emailAddressChanged) {
            return true;
        }
        else {
            return false;
        }
    }, [emailAddressChanged, verifyEmailInProgress, hasVerifiedEmail]);

    const verifyEmailAddress = async () => {
        setShowNotVerifiedWarning(false);
        setVerifyEmailInProgress(true);
        // validate email address
        form.current.cleanErrorForField('email');
        const emailValid = form.current.checkForField('email');

        // check if account already exists
        if (emailValid) {
            const emailAddress = formValues['email'];
            const result = await dispatch(validateCognitoEmailAddress(emailAddress));
            setAccountAlreadyExistsWithEmail(result.userExists);
            setHasVerifiedEmail(true);
        }
        setVerifyEmailInProgress(false);
    }

    const handleEmailOptionChanged = (value) => {
        setFormValues(prevValues => (
            {
                ...prevValues,
                updateAllPermits: value
            }
        ));
    };

    const selectedUpdateAccountOption = useMemo(() => {
        if (!emailAddressChanged) {
            return ADMIN_UPDATE_ACCOUNT_OPTIONS_ENUM.NOCHANGE;
        }
        else if (accountAlreadyExistsWithEmail) {
            return ADMIN_UPDATE_ACCOUNT_OPTIONS_ENUM.MOVETOEXISTINGACCOUNT;
        }
        else {
            return ADMIN_UPDATE_ACCOUNT_OPTIONS_ENUM.NEWACCOUNT;
        }
    }, [emailAddressChanged, accountAlreadyExistsWithEmail]);

    const updatePortalAccount = () => {
        const isValid = form.current.check();

        setShowNotVerifiedWarning(!hasVerifiedEmail && emailAddressChanged);

        if (isValid && (hasVerifiedEmail || !emailAddressChanged)) {
            dispatch(
                updatePermitData({
                    updateAccountOption: selectedUpdateAccountOption,
                    updateAllPermits: formValues.updateAllPermits,
                    registrant: {
                        ...registrant,
                        registrant: {
                            ...initialRegistrantData, // always reset back to initial values
                            email: formValues.email,
                        },
                    },
                })
            );
            return true;
        }
        else {
            return false;
        }
    }

    const handleBackClick = () => {
        // reset selections
        dispatch(
            updatePermitData({
                updateAccountOption: ADMIN_UPDATE_ACCOUNT_OPTIONS_ENUM.NOCHANGE,
                updateAllPermits: formValues.updateAllPermits,
                registrant: {
                    ...registrant,
                    registrant: {
                        ...initialRegistrantData, // always reset back to initial values
                    },
                },
            })
        );
        return true;
    }

    return (
        <FlexboxGrid justify="space-between">
            <FlexboxGrid.Item colspan={24}>
                <PortalHeader flow={FLOWS_ENUM.ADMIN_UPDATE} showAccountDropdown={false} />
            </FlexboxGrid.Item>
            <FlexboxGrid.Item colspan={24}>
                <Content>
                    <FlowPageLayout
                        title="Portal Account"
                        onNext={() => updatePortalAccount()}
                        onBack={handleBackClick}
                    >
                        <Grid className="ml-0 " xs={24} lg={12} fluid>
                            <Row>
                                <Col xs={24}>
                                    <Text className='paragraph'>
                                        Registrants use their email address to login to the Customer Portal,
                                        which allows them access to all {pluralize(licenseTypeVerbiage.toLowerCase())} linked to that email address.
                                    </Text>
                                    <Text className='paragraph'>
                                        When updating the registrant's email address, you will need to indicate whether you wish to update the email address
                                        (and thus the login) for only this {pluralize(licenseTypeVerbiage.toLowerCase())}, or for all
                                        {pluralize(licenseTypeVerbiage.toLowerCase())} attached to this email address.
                                        Note that if you update only this {pluralize(licenseTypeVerbiage.toLowerCase())}, you will be moving the
                                        {pluralize(licenseTypeVerbiage.toLowerCase())} to a new Customer Portal Account with the new Email Address as a login.
                                    </Text>
                                </Col>
                            </Row>

                            <Row className='mt-16'>
                                <Col xs={24}>
                                    <Form
                                        model={formModel}
                                        ref={form}
                                        checkTrigger="none"
                                        formDefaultValue={formValues}
                                        formValue={formValues}
                                        onCheck={(e) => {
                                            setFormError(e);
                                        }}
                                        onChange={(values) => {
                                            if (formValues.email !== values.email) {
                                                setHasVerifiedEmail(false);
                                                setAccountAlreadyExistsWithEmail(false);
                                                // clear email error
                                                form.current.cleanErrorForField('email');
                                                const { email, ...rest } = formError;
                                                setFormError(rest);
                                            }
                                            setFormValues({
                                                ...formValues,
                                                ...values
                                            })
                                        }}
                                        fluid
                                    >
                                        <Grid fluid>
                                            <Row className='mb-24'>
                                                <Col xs={24}>
                                                    <Text>Registrant Email + Portal Login</Text>
                                                </Col>
                                                <Col xs={24} md={12}>
                                                    <Stack as={"div"} direction="row" spacing={8}>
                                                        <Stack.Item style={{ width: '100%' }}>
                                                            <FormTextField
                                                                name="email"
                                                                /*If we validate an individual form element rather than the entire form the form errors object is different*/
                                                                hasError={formError['email'] && formError['email']?.hasError !== false}
                                                                validationRule={validationModel.email}
                                                                errorMessage={formError['email']}
                                                                value={formValues?.email ?? ""}
                                                                inputFieldType='email'
                                                            />
                                                        </Stack.Item>
                                                        <Stack.Item>
                                                            {
                                                                showCheck && (
                                                                    <CheckRound
                                                                        style={{
                                                                            color: 'green',
                                                                            fontSize: "20px"
                                                                        }}
                                                                    />
                                                                )
                                                            }
                                                            {
                                                                verifyEmailInProgress && (
                                                                    <IconIndicator size={20} />
                                                                )
                                                            }
                                                            {
                                                                showVerifyButton && (
                                                                    <Button onClick={() => { verifyEmailAddress() }}>Verify</Button>
                                                                )
                                                            }
                                                        </Stack.Item>
                                                    </Stack>
                                                </Col>
                                                {
                                                    accountAlreadyExistsWithEmail && (
                                                        <Col xs={24} md={12}>
                                                            <Text>This email address is linked to an existing Portal Account and {pluralize(licenseTypeVerbiage.toLowerCase())} will be merged to the existing account.</Text>
                                                        </Col>
                                                    )
                                                }
                                            </Row>
                                            {
                                                emailAddressChanged && (
                                                    <Row className="mb-12">
                                                        <Col xs={24}>
                                                            <RadioQuestionGroup
                                                                description='Email Update Behaviour'
                                                                key={`rdoEmailOption`}
                                                                name={`updateAllPermits`}
                                                                options={emailUpdateOptions}
                                                                defaultOption={emailUpdateOptions.find(o => o.key === formValues.updateAllPermits)}
                                                                value={emailUpdateOptions.find(o => o.key === formValues.updateAllPermits)}
                                                                onChange={(value) => {
                                                                    handleEmailOptionChanged(value);
                                                                }}
                                                                hasError={formError["updateAllPermits"]}
                                                                validationRule={validationModel.updateAllPermits}
                                                                errorMessage={'An option must be selected'}
                                                                className="mb-4">
                                                            </RadioQuestionGroup>
                                                        </Col>
                                                    </Row>
                                                )
                                            }
                                        </Grid>
                                    </Form>
                                </Col>
                            </Row>
                            {
                                formValues.updateAllPermits === C_ALL_PERMITS && (
                                    <Row className={showNotVerifiedWarning ? 'mb-24' : ''}>
                                        <Col xs={24}>
                                            {
                                                isLoading && <IconIndicator />
                                            }
                                            {
                                                !isLoading && emailAddressChanged && <Notice
                                                    type="warning"
                                                    message={linkedPermitsWarning}
                                                    iconSize={16}
                                                    isMessageComponent={true}
                                                />
                                            }
                                        </Col>
                                    </Row>
                                )
                            }
                            {
                                showNotVerifiedWarning && (
                                    <Row>
                                        <Col xs={24}>
                                            <Notice message="You must vefify the Registrant Email + Portal Login before proceeding."></Notice>
                                        </Col>
                                    </Row>
                                )
                            }
                        </Grid>
                    </FlowPageLayout>
                </Content>
            </FlexboxGrid.Item>
        </FlexboxGrid >
    )
}

export default AdminUpdatePortalAccount
